/**
 * SignQueueRow — pending / signed signature row
 *
 * Two variants in one component:
 * - variant="pending" → Urgent chip (when urgency==='high'), Review + Sign buttons.
 * - variant="signed"  → no Urgent chip, "Signed {date}" line, single Download button.
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (row pattern)
 *
 * Rules:
 * - Glass components only (rule 11b)
 * - sx + theme tokens, alpha() for thumbnail tint (rule 11c)
 * - All copy via t() (rule 13)
 * - 44px touch target on every interactive (portals.md)
 */

import DownloadIcon from '@mui/icons-material/Download';
import EastIcon from '@mui/icons-material/East';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  alpha,
  Box,
  baseColors,
  GlassButton,
  GlassCard,
  GlassChip,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';

type Urgency = 'high' | 'normal';

export interface SignQueueRowPendingProps {
  variant: 'pending';
  id: string;
  title: string;
  loan: string;
  sentBy: string;
  sentAt: string;
  pages: number;
  urgency: Urgency;
  onReview: () => void;
  onSign: () => void;
  signDisabled?: boolean;
  signDisabledReason?: string;
}

export interface SignQueueRowSignedProps {
  variant: 'signed';
  id: string;
  title: string;
  loan: string;
  signedAt: string;
  pages: number;
  onDownload: () => void;
  downloadDisabled?: boolean;
}

export type SignQueueRowProps = SignQueueRowPendingProps | SignQueueRowSignedProps;

function FileTypeThumbnail({ tone, label }: { tone: 'mint' | 'red' | 'neutral'; label: string }) {
  const bgColor =
    tone === 'red'
      ? alpha(baseColors.red[500], 0.16)
      : tone === 'mint'
        ? alpha(baseColors.teal[400], 0.16)
        : alpha(baseColors.neutral[500], 0.12);
  const labelColor = tone === 'red' ? 'error.main' : tone === 'mint' ? 'success.main' : 'text.secondary';
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 40,
        height: 50,
        borderRadius: 0.75,
        bgcolor: bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" sx={{ color: labelColor, fontWeight: 700, letterSpacing: '0.08em' }}>
        {label.toUpperCase()}
      </Typography>
    </Box>
  );
}

export function SignQueueRow(props: SignQueueRowProps) {
  const { t } = useI18n();
  const isPending = props.variant === 'pending';
  const showUrgent = isPending && props.urgency === 'high';
  // Pending = red label thumbnail (signals action required), signed = mint
  const thumbTone: 'red' | 'mint' = isPending ? 'red' : 'mint';
  // Title displayed inside the thumbnail (first three chars of "PDF" / "DOC" etc.)
  const thumbLabel = 'PDF';

  return (
    <GlassCard
      component="article"
      role="article"
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        gap: 2,
      }}
    >
      <FileTypeThumbnail tone={thumbTone} label={thumbLabel} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {props.title}
          </Typography>
          {showUrgent && (
            <GlassChip
              label={t('consumer.signQueue.chipUrgent')}
              size="small"
              color="error"
              aria-label={t('consumer.signQueue.chipUrgent')}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center', color: 'text.secondary' }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {props.loan}
          </Typography>
          <Typography variant="caption">{t('consumer.signQueue.rowPages', { count: props.pages })}</Typography>
          {isPending ? (
            <Typography variant="caption">
              {t('consumer.signQueue.rowSentBy', { sender: props.sentBy, date: props.sentAt })}
            </Typography>
          ) : (
            <Typography variant="caption">{t('consumer.signQueue.rowSignedAt', { date: props.signedAt })}</Typography>
          )}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        {isPending ? (
          <>
            <GlassButton
              size="small"
              variant="text"
              startIcon={<VisibilityIcon />}
              onClick={props.onReview}
              sx={{ minHeight: 44 }}
            >
              {t('consumer.signQueue.actionReview')}
            </GlassButton>
            <GlassButton
              size="small"
              variant="gradient"
              endIcon={<EastIcon />}
              onClick={props.onSign}
              disabled={props.signDisabled}
              aria-label={props.signDisabledReason ?? t('consumer.signQueue.actionSign')}
              sx={{ minHeight: 44 }}
            >
              {t('consumer.signQueue.actionSign')}
            </GlassButton>
          </>
        ) : (
          <GlassButton
            size="small"
            variant="text"
            startIcon={<DownloadIcon />}
            onClick={props.onDownload}
            disabled={props.downloadDisabled}
            sx={{ minHeight: 44 }}
          >
            {t('consumer.signQueue.actionDownload')}
          </GlassButton>
        )}
      </Stack>
    </GlassCard>
  );
}

export default SignQueueRow;
